import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  getProductIdsFromUrl,
  updateUrlWithBookingParams,
} from "@/utils/urlUtils";

export interface Discount {
  code: string;
  type: "percentage" | "fixed_amount";
  value: number;
  // Stripe coupon duration — "once" applies to the first payment only, while
  // "repeating"/"forever" recur. Drives how the voucher is labelled on a
  // recurring membership. Undefined for codes with no duration info (referral).
  duration?: "once" | "repeating" | "forever" | null;
  durationInMonths?: number | null;
}

interface BookingState {
  // View mode
  viewMode: "packages" | "configure";
  setViewMode: (mode: "packages" | "configure") => void;

  // Feature flags
  showBloodOnlyToggle: boolean;
  setShowBloodOnlyToggle: (show: boolean) => void;

  // Package type selection (mri vs blood-only)
  selectedPackageType: string;
  setSelectedPackageType: (packageType: string) => void;

  // Selected products (main booking state)
  selectedProducts: string[];
  setSelectedProducts: (products: string[]) => void;
  addProduct: (productId: string) => void;
  removeProduct: (productId: string) => void;

  // Selected add-ons
  selectedAddOns: string[];
  setSelectedAddOns: (addOns: string[]) => void;
  toggleAddOn: (addOnId: string) => void;

  // Configure mode specific selections
  configureProducts: string[];
  setConfigureProducts: (products: string[]) => void;
  addConfigureProduct: (productId: string) => void;
  removeConfigureProduct: (productId: string) => void;

  // Package selection
  selectedPackageId: string | null;
  setSelectedPackageId: (packageId: string | null) => void;

  // Invitation code and discount
  invitationCode: string;
  discount: Discount | null;
  setInvitationCode: (code: string) => void;
  setDiscount: (discount: Discount | null) => void;
  clearInvitationCode: () => void;

  // URL initialization tracking
  hasInitializedFromUrl: boolean;
  initializeFromUrl: (
    products?: {
      id: string;
      product_type?: string;
      package_types?: string[];
    }[],
    isBuildYourOwnEnabled?: boolean,
  ) => void;

  // Reset functions
  resetState: () => void;
  resetProducts: () => void;
}

const initialState = {
  viewMode: "packages" as const,
  showBloodOnlyToggle: false,
  selectedPackageType: "mri" as const,
  selectedProducts: [],
  selectedAddOns: [],
  configureProducts: [],
  selectedPackageId: null,
  invitationCode: "",
  discount: null,
  hasInitializedFromUrl: false,
};

export const useBookingStore = create<BookingState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // View mode actions
      setViewMode: (mode) => {
        console.log("BookingStore - Setting view mode:", mode);
        set({ viewMode: mode });
      },

      // Feature flag actions
      setShowBloodOnlyToggle: (show) => {
        console.log("BookingStore - Setting show blood-only toggle:", show);
        set({ showBloodOnlyToggle: show });
      },

      // Package type actions
      setSelectedPackageType: (packageType) => {
        console.log("BookingStore - Setting package type:", packageType);
        set({ selectedPackageType: packageType });
        // Reset selected products when switching package type
        set({
          selectedProducts: [],
          selectedPackageId: null,
          selectedAddOns: [],
        });
      },

      // Selected products actions
      setSelectedProducts: (products) => {
        console.log("BookingStore - Setting selected products:", products);
        set({ selectedProducts: products });
      },

      addProduct: (productId) => {
        const { selectedProducts } = get();
        if (!selectedProducts.includes(productId)) {
          const newProducts = [...selectedProducts, productId];
          console.log(
            "BookingStore - Adding product:",
            productId,
            "New selection:",
            newProducts,
          );
          set({ selectedProducts: newProducts });
        }
      },

      removeProduct: (productId) => {
        const { selectedProducts, configureProducts, viewMode } = get();
        const newProducts = selectedProducts.filter((id) => id !== productId);
        console.log(
          "BookingStore - Removing product:",
          productId,
          "New selection:",
          newProducts,
        );
        set({ selectedProducts: newProducts });

        // Also remove from configure products if in configure mode
        if (viewMode === "configure" && configureProducts.includes(productId)) {
          const newConfigureProducts = configureProducts.filter(
            (id) => id !== productId,
          );
          console.log(
            "BookingStore - Removing from configure products:",
            productId,
            "New configure selection:",
            newConfigureProducts,
          );
          set({ configureProducts: newConfigureProducts });
        }
      },

      // Selected add-ons actions
      setSelectedAddOns: (addOns) => {
        console.log("BookingStore - Setting selected add-ons:", addOns);
        set({ selectedAddOns: addOns });
      },

      toggleAddOn: (addOnId) => {
        const { selectedAddOns, configureProducts } = get();
        const isSelected = selectedAddOns.includes(addOnId);

        if (isSelected) {
          const newAddOns = selectedAddOns.filter((id) => id !== addOnId);
          console.log(
            "BookingStore - Removing add-on:",
            addOnId,
            "New selection:",
            newAddOns,
          );
          set({ selectedAddOns: newAddOns });

          // Also remove from configure products if present
          if (configureProducts.includes(addOnId)) {
            const newConfigureProducts = configureProducts.filter(
              (id) => id !== addOnId,
            );
            console.log(
              "BookingStore - Removing add-on from configure products:",
              addOnId,
            );
            set({ configureProducts: newConfigureProducts });
          }
        } else {
          const newAddOns = [...selectedAddOns, addOnId];
          console.log(
            "BookingStore - Adding add-on:",
            addOnId,
            "New selection:",
            newAddOns,
          );
          set({ selectedAddOns: newAddOns });
        }
      },

      // Configure mode actions
      setConfigureProducts: (products) => {
        console.log("BookingStore - Setting configure products:", products);
        set({ configureProducts: products });
      },

      addConfigureProduct: (productId) => {
        const { configureProducts } = get();
        if (!configureProducts.includes(productId)) {
          const newProducts = [...configureProducts, productId];
          console.log(
            "BookingStore - Adding configure product:",
            productId,
            "New selection:",
            newProducts,
          );
          set({ configureProducts: newProducts });
        }
      },

      removeConfigureProduct: (productId) => {
        const { configureProducts } = get();
        const newProducts = configureProducts.filter((id) => id !== productId);
        console.log(
          "BookingStore - Removing configure product:",
          productId,
          "New selection:",
          newProducts,
        );
        set({ configureProducts: newProducts });
      },

      // Package selection actions
      setSelectedPackageId: (packageId) => {
        console.log("BookingStore - Setting selected package:", packageId);
        set({ selectedPackageId: packageId });
      },

      // Invitation code actions
      setInvitationCode: (code) => {
        console.log("BookingStore - Setting invitation code:", code);
        set({ invitationCode: code });
      },

      setDiscount: (discount) => {
        console.log("BookingStore - Setting discount:", discount);
        set({ discount });
      },

      clearInvitationCode: () => {
        console.log("BookingStore - Clearing invitation code");
        set({ invitationCode: "", discount: null });
      },

      // URL initialization
      initializeFromUrl: (products, isBuildYourOwnEnabled = true) => {
        const { hasInitializedFromUrl } = get();

        // Prevent re-initialization if already done
        if (hasInitializedFromUrl) {
          console.log("BookingStore - Already initialized from URL, skipping");
          return;
        }

        const urlProductIds = getProductIdsFromUrl();
        const urlParams = new URLSearchParams(window.location.search);
        const urlPackageType = urlParams.get("packageType");

        // Set package type from URL if provided
        if (urlPackageType) {
          console.log(
            "BookingStore - Setting package type from URL:",
            urlPackageType,
          );
          set({ selectedPackageType: urlPackageType });
        }

        if (urlProductIds.length > 0 && products) {
          console.log(
            "BookingStore - Initializing from URL products:",
            urlProductIds,
          );

          // Check if any URL products are blood-only packages
          const hasBloodOnlyPackageInUrl = products.some(
            (p) =>
              urlProductIds.includes(p.id) &&
              p.product_type === "package" &&
              p.package_types?.includes("blood-only"),
          );

          // If blood-only product is directly requested via URL, allow access to the toggle
          if (hasBloodOnlyPackageInUrl) {
            console.log(
              "BookingStore - Blood-only product found in direct URL, showing toggle",
            );
            set({
              showBloodOnlyToggle: true,
              selectedPackageType: "blood-only",
            });
          }

          // Check if any URL products are packages
          const hasPackageInUrl = products.some(
            (p) => urlProductIds.includes(p.id) && p.product_type === "package",
          );

          if (hasPackageInUrl) {
            console.log(
              "BookingStore - Found package in URL, setting packages mode",
            );
            const packageId =
              products.find(
                (p) =>
                  urlProductIds.includes(p.id) && p.product_type === "package",
              )?.id || null;

            // Infer package type from selected package if not in URL
            if (!urlPackageType && packageId) {
              const selectedPackage = products.find((p) => p.id === packageId);
              const inferredPackageType = selectedPackage?.package_types?.[0];
              if (inferredPackageType) {
                console.log(
                  "BookingStore - Inferring package type from selected package:",
                  inferredPackageType,
                );
                set({ selectedPackageType: inferredPackageType });
              }
            }

            set({
              viewMode: "packages",
              selectedProducts: urlProductIds,
              selectedPackageId: packageId,
              hasInitializedFromUrl: true,
            });
          } else {
            // Individual products found - would normally trigger configure mode
            // But if build_your_own is disabled, redirect to packages mode with no selection
            if (!isBuildYourOwnEnabled) {
              console.log(
                "BookingStore - Found individual products in URL but build_your_own is disabled, redirecting to packages mode",
              );
              set({
                viewMode: "packages",
                selectedProducts: [],
                selectedPackageId: null,
                hasInitializedFromUrl: true,
              });
            } else {
              console.log(
                "BookingStore - Found individual products in URL, setting configure mode",
              );
              set({
                viewMode: "configure",
                selectedProducts: urlProductIds,
                configureProducts: urlProductIds,
                selectedPackageId: null,
                hasInitializedFromUrl: true,
              });
            }
          }

          // Clear the products parameter from URL after successful initialization
          console.log("BookingStore - Clearing products parameter from URL");
          updateUrlWithBookingParams({
            products: "", // This will remove the products parameter
          });
        }
      },

      // Reset actions
      resetState: () => {
        console.log("BookingStore - Resetting all state");
        set(initialState);
      },

      resetProducts: () => {
        console.log("BookingStore - Resetting product selections");
        set({
          selectedProducts: [],
          selectedAddOns: [],
          configureProducts: [],
          selectedPackageId: null,
          hasInitializedFromUrl: false, // Reset the initialization flag
        });
      },
    }),
    {
      name: "booking-store",
    },
  ),
);
